import { useRef, useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { Printer, Loader2, X } from 'lucide-react';
import { useVertical } from '../../../context/VerticalContext';
import { reportError } from '../../../lib/errors';
import { mileageLine } from '../Customers/mileage';
import { DEFAULT_DOCUMENT_ACCENT } from '../../../lib/brandAccent';

interface QuoteDocumentProps {
    quote: any;
    onClose: () => void;
}

export default function QuoteDocument({ quote, onClose }: QuoteDocumentProps) {
    const { activeOrgId } = useVertical();
    const [branding, setBranding] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const printRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fetchBranding = async () => {
            if (!activeOrgId) return;
            const { data, error: fetchError } = await supabase
                .from('organizations')
                .select('branding, name')
                .eq('id', activeOrgId)
                .single();
            if (fetchError) reportError(fetchError, 'quote-document.organizations');

            if (data) {
                setBranding({
                    ...data.branding,
                    name: data.name
                });
            }
            setLoading(false);
        };
        fetchBranding();
    }, [activeOrgId]);

    const handlePrint = () => {
        window.print();
    };

    if (loading) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/80">
                <Loader2 className="w-8 h-8 text-fg animate-spin" />
            </div>
        );
    }

    const customer = quote.metadata.customer;
    const vehicle = quote.metadata.vehicle;
    // An MOT-sourced figure always prints with its qualifier (Customers/mileage).
    const mileageText = mileageLine(vehicle.mileage, vehicle.mileageSource, vehicle.mileageMotDate);
    const lineItems = quote.metadata.lineItems || [];
    const total = quote.total_amount || 0;
    // Audit finding M-06. This used to be `subtotal = total / 1.2`,
    // unconditionally — so a NON-VAT-REGISTERED sole trader's quote displayed
    // £83.33 of VAT on a £500 job. Under VATA 1994 s.67, issuing a document
    // showing VAT when unregistered is a penalty offence, and this component
    // also backs the customer-facing public quote view.
    //
    // QuoteBuilder already asks the backend for the rate
    // (fn_get_applicable_vat_rate, default 0) and stores total = subtotal + vat.
    // The rate itself is not persisted on the quote, so derive the split from
    // the line items, which are net (quantity x unitPrice). That is exact for
    // both cases: unregistered -> line sum equals total -> vat is 0 and the row
    // is hidden; registered -> the difference is the VAT actually charged, at
    // whatever rate applied. Verified against production: 6 of 11 quotes have
    // total_amount exactly equal to their line sum, i.e. genuinely zero VAT.
    const subtotal = lineItems.length > 0
        ? lineItems.reduce(
            (sum: number, li: any) =>
                sum + (Number(li.quantity) || 0) * (Number(li.unitPrice ?? li.unit_price) || 0),
            0,
          )
        : total;
    const vat = Math.round((total - subtotal) * 100) / 100;
    const showVat = Math.abs(vat) >= 0.01;
    const date = new Date(quote.created_at);
    const validUntil = new Date(date);
    validUntil.setDate(validUntil.getDate() + 30); // Valid for 30 days

    const accentColor = branding.invoice_accent_color || branding.primary_color || DEFAULT_DOCUMENT_ACCENT;
    const isMinimal = branding.invoice_theme === 'minimal';

    return (
        <div className="fixed inset-0 z-50 flex flex-col bg-scrim/80 backdrop-blur-sm p-4 overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-4 max-w-4xl mx-auto w-full">
                <h2 className="text-fg font-bold text-lg">Quote Preview</h2>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handlePrint}
                        className="flex items-center gap-2 px-4 py-2 bg-card text-fg rounded-lg hover:bg-pressed transition-colors font-bold text-sm"
                    >
                        <Printer className="w-4 h-4" />
                        Print / Save as PDF
                    </button>
                    <button
                        onClick={onClose}
                        className="p-2 text-fg-4 hover:text-fg hover:bg-tint/10 rounded-lg"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Document Preview Area */}
            <div className="flex-1 overflow-y-auto w-full">
                <div data-theme="paper" className="max-w-[210mm] mx-auto min-h-[297mm] bg-card text-fg shadow-2xl relative">
                    <div ref={printRef} className="h-full flex flex-col">

                        {/* Header */}
                        <div
                            className="p-12 pb-8 flex justify-between items-start border-b border-line"
                            style={isMinimal
                                ? { backgroundColor: 'var(--fd-surface)', color: 'var(--fd-text-bright)', borderBottom: `4px solid ${accentColor}` }
                                : { backgroundColor: accentColor, color: 'var(--fd-on-fill)' }
                            }
                        >
                            <div className="flex gap-4">
                                {branding.logo_url && (
                                    <img
                                        src={branding.logo_url}
                                        alt="Logo"
                                        className={`w-20 h-20 object-contain rounded-lg p-1 ${isMinimal ? '' : 'bg-card'}`}
                                    />
                                )}
                                <div>
                                    <h1 className="text-2xl font-bold transition-colors">{branding.name}</h1>
                                    <div className={`text-sm mt-2 space-y-0.5 whitespace-pre-line ${isMinimal ? 'text-fg-4' : 'text-fg/90'}`}>
                                        {branding.address}
                                    </div>
                                    <div className={`text-sm mt-2 space-y-0.5 ${isMinimal ? 'text-fg-4' : 'text-fg/90'}`}>
                                        {branding.phone_number && <p>📞 {branding.phone_number}</p>}
                                        {branding.contact_email && <p>✉️ {branding.contact_email}</p>}
                                        {branding.website_url && <p>🌐 {branding.website_url}</p>}
                                    </div>
                                </div>
                            </div>
                            <div className="text-right">
                                <h2 className={`text-4xl font-bold tracking-tight uppercase ${isMinimal ? 'text-fg-4' : 'text-fg/30'}`}>Quote</h2>
                                <div className="mt-4 space-y-1 text-sm">
                                    <p className="flex justify-end gap-4">
                                        <span className="text-fg-4">Date:</span>
                                        <span className="font-mono font-bold">{date.toLocaleDateString('en-GB')}</span>
                                    </p>
                                    <p className="flex justify-end gap-4">
                                        <span className="text-fg-4">Valid Until:</span>
                                        <span className="font-mono font-bold">{validUntil.toLocaleDateString('en-GB')}</span>
                                    </p>
                                    <p className="flex justify-end gap-4">
                                        <span className="text-fg-4">Quote Reference:</span>
                                        <span className="font-mono font-bold">#{quote.public_token?.slice(0, 8).toUpperCase()}</span>
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Customer & Vehicle */}
                        <div className="p-12 py-8 flex justify-between">
                            <div>
                                <h3 className="text-xs font-bold text-fg-4 uppercase tracking-wider mb-2">Quote For</h3>
                                <p className="text-lg font-bold text-fg">{customer.name}</p>
                                {customer.phone && <p className="text-sm text-fg-3">{customer.phone}</p>}
                                {customer.email && <p className="text-sm text-fg-3">{customer.email}</p>}
                            </div>
                            <div className="text-right">
                                <h3 className="text-xs font-bold text-fg-4 uppercase tracking-wider mb-2">Vehicle Details</h3>
                                <p className="text-lg font-bold text-fg font-mono">{vehicle.vrm}</p>
                                <p className="text-sm text-fg-3">
                                    {vehicle.make} {vehicle.model}
                                    {vehicle.engine ? ` - ${vehicle.engine}` : ''}
                                    {vehicle.fuelType ? ` (${vehicle.fuelType})` : ''}
                                </p>
                                <div className="text-sm text-fg-4 mt-1">
                                    {vehicle.year && <span>{vehicle.year}</span>}
                                    {vehicle.year && mileageText && <span> • </span>}
                                    {mileageText && <span>{mileageText}</span>}
                                </div>
                            </div>
                        </div>

                        {/* Line Items */}
                        <div className="px-12">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b-2 border-line text-xs text-fg-4 uppercase tracking-wider text-left">
                                        <th className="py-3 font-medium">Description</th>
                                        <th className="py-3 font-medium text-center">Type</th>
                                        <th className="py-3 font-medium text-center">Qty</th>
                                        <th className="py-3 font-medium text-right">Unit Price</th>
                                        <th className="py-3 font-medium text-right">Total</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-line">
                                    {lineItems.map((item: any, i: number) => (
                                        <tr key={i}>
                                            <td className="py-4 text-sm font-medium text-fg">
                                                {item.description}
                                            </td>
                                            <td className="py-4 text-center">
                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${item.type === 'labour' ? 'bg-gold/10 text-gold' : 'bg-info/10 text-info'
                                                    }`}>
                                                    {item.type}
                                                </span>
                                            </td>
                                            <td className="py-4 text-center text-sm text-fg-3 font-mono">
                                                {item.quantity}
                                            </td>
                                            <td className="py-4 text-right text-sm text-fg-3 font-mono">
                                                £{item.unitPrice.toFixed(2)}
                                            </td>
                                            <td className="py-4 text-right text-sm font-bold text-fg font-mono">
                                                £{(item.quantity * item.unitPrice).toFixed(2)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Totals */}
                        <div className="px-12 py-8 flex justify-end">
                            <div className="w-64 space-y-3">
                                <div className="flex justify-between text-sm text-fg-4">
                                    <span>Subtotal</span>
                                    <span className="font-mono">£{subtotal.toFixed(2)}</span>
                                </div>
                                {showVat && (
                                    <div className="flex justify-between text-sm text-fg-4">
                                        <span>VAT</span>
                                        <span className="font-mono">£{vat.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="pt-3 border-t-2 border-line flex justify-between items-baseline">
                                    <span className="font-bold text-fg">Total</span>
                                    <span className="text-2xl font-bold font-mono text-accent" style={{ color: accentColor }}>
                                        £{total.toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Notes */}
                        {quote.metadata.notes && (
                            <div className="px-12 py-6 border-t border-line">
                                <h4 className="text-xs font-bold text-fg-4 uppercase mb-2">Notes</h4>
                                <p className="text-sm text-fg-3 whitespace-pre-wrap">{quote.metadata.notes}</p>
                            </div>
                        )}

                        {/* Footer */}
                        <div className="mt-auto p-12 bg-raised text-center text-xs text-fg-4">
                            <p>This quote is valid for 30 days from the date of issue.</p>
                            <div className="mt-4 pt-4 border-t border-line flex items-center justify-center gap-2 opacity-50">
                                <img src="/forgedash-logo.svg" className="h-4" alt="" />
                                <span>Powered by ForgeDash</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Print Styles */}
            <style>{`
                @media print {
                    body > *:not([role="dialog"]) {
                        display: none !important;
                    }
                    /* Hide everything in the component except the print area */
                    .fixed {
                        position: static !important;
                        background: none !important;
                        padding: 0 !important;
                        overflow: visible !important;
                        z-index: auto !important;
                    }
                    /* Hide toolbar */
                    .flex.items-center.justify-between.mb-4,
                    button {
                        display: none !important;
                    }
                    /* Reset scroll area */
                    .flex-1.overflow-y-auto {
                        height: auto !important;
                        width: 100% !important;
                        overflow: visible !important;
                    }
                    /* Page setup */
                    .max-w-\\[210mm\\] {
                        max-width: none !important;
                        width: 100% !important;
                        min-height: auto !important;
                        box-shadow: none !important;
                        margin: 0 !important;
                    }
                    @page {
                        margin: 0;
                        size: auto;
                    }
                    
                }
            `}</style>
        </div>
    );
}
